import React, { useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../../components/layout/PageHeader";
import { useUnsavedChanges } from "../../../components/layout/UnsavedChanges";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import {
  BRAND_PLANS_PATH,
  BRAND_SERVICES_PATH,
  ROUTES,
} from "../../../routes/paths";
import PlansRequiredModal from "../../brand/components/PlansRequiredModal";
import ServicesRequiredModal from "../../brand/components/ServicesRequiredModal";
import { usePlans } from "../../brand/hooks/usePlans";
import AddClientForm from "../components/AddClientForm";
import { toCreateClientPayload } from "../clientPayload";
import { useClientLookups } from "../hooks/useClientLookups";
import { createClient } from "../services/clientsService";
import { NewClient } from "../types/clients.types";
import { validateNewClient } from "../validation";

const EMPTY_CLIENT: NewClient = {
  title: "",
  firstName: "",
  lastName: "",
  gender: "",
  dob: "",
  profession: "",
  lifestyle: "",
  address: "",
  state: "",
  city: "",
  pincode: "",
  phone: "",
  whatsapp: "",
  whatsappSameAsPhone: false,
  email: "",
  goals: [],
  foodPreference: "",
  foodAllergies: [],
  planId: "",
  paidPrice: "",
  purchasedDate: "",

  // The optional group behind the fold. The two units start on what almost
  // every client is measured in here — they are the unit beside an empty box
  // rather than an answer, and nothing is sent for either until there is a
  // figure in front of it.
  height: "",
  heightUnit: "cm",
  weight: "",
  weightUnit: "kg",
  targetWeight: "",
  targetWeightUnit: "kg",
  cuisines: [],
  dishesLoved: [],
  dishesAvoided: [],
  conditions: [],
  onMedication: "",
  medications: [],
  workFrom: "",
  workTo: "",
  workAnyHours: false,
  sleepFrom: "",
  sleepTo: "",
  sleepAnyHours: false,
  travel: "",
  alcohol: "",
  smoking: "",
};

const AddClientPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { getToken } = useAuth();
  const [client, setClient] = useState<NewClient>(EMPTY_CLIENT);
  // Held so the action bar can say it is working and refuse a second press —
  // a double-submitted form is two clients.
  const [saving, setSaving] = useState(false);

  // Title, Goal, Food preference, Food allergies and Life style are the
  // backend's lists rather than ours, so they are fetched as the page opens;
  // Gender, Profession, State and City are fixed sets the app carries itself.
  const lookups = useClientLookups();

  // The Subscription details section sells one of these, so the same list the
  // Create new payment dialog offers is fetched here — and read the same way,
  // name and duration together.
  const { plans, status: plansStatus } = usePlans();

  // A plan that is not listed cannot be sold: an in-active one is off the
  // public page, and recording a subscription against it would sell it anyway.
  const sellable = useMemo(
    () => plans.filter((plan) => plan.status === "Active"),
    [plans]
  );

  // Nothing to sell, so nothing to add this client against: the Plan field
  // would open on an empty list and the form could not be finished. The list
  // screen turns its Add client buttons into the same prompt, so this is the
  // door somebody reaches by typing the URL, by a bookmark, or by coming back
  // to a tab left open since before the last plan was retired.
  //
  // Read off the list already in hand rather than by asking again, and off
  // `sellable` rather than `plans`: an in-active plan is off the public
  // page and cannot be sold, so a workspace holding nothing but those is in
  // exactly the position this is about. `status === "ready"` is what keeps the
  // prompt off a page whose plans are merely still loading — see
  // `useSetupGate` for why that caution matters.
  const noPlans = plansStatus === "ready" && sellable.length === 0;

  // The other half of the same rule: the Goal field picks from the practice's
  // own active services, so with none listed it opens on an empty list and this
  // form cannot be finished either.
  //
  // Read off the lookups the form is already holding rather than through
  // `useServicesCreated`, for the reason the plans answer is read off
  // `sellable`: the list is in hand, and asking again would be the same request
  // twice. What it holds is already the active ones — `fetchServiceGoals` asks
  // for those alone.
  const goals = lookups.serviceGoals;
  const noServices = goals.status === "ready" && goals.options.length === 0;

  // What the picked plan lists at, which is the ceiling the Paid price is
  // checked against — anything above it is a rise rather than a discount.
  const pickedPrice = sellable.find(
    (plan) => plan.id === client.planId
  )?.price;

  const errors = validateNewClient(client, pickedPrice);
  const valid = Object.keys(errors).length === 0;

  // The form's own way of marking up what is still missing, so the rail's
  // prompt can offer to save a form that cannot be saved yet and point at why.
  const problems = useRef<(() => void) | null>(null);

  // Anything at all typed into the form is something a navigation would throw
  // away — there is no draft behind this screen, so the empty form it opened
  // with is the whole of what counts as nothing to lose. Untouched, the rail
  // stays as quick as it has always been.
  useUnsavedChanges(
    JSON.stringify(client) !== JSON.stringify(EMPTY_CLIENT),
    {
      title: "Unsaved client details",
      body: "You’ve entered details for this client but haven’t saved them yet. Save your changes, or they’ll be discarded.",
      confirmLabel: "Save & add",
    },
    // The Save row's own answer, handed to the prompt so its button does what
    // it says. `commit` is called through an arrow rather than passed: it is
    // declared below, and the literal is built as this renders.
    {
      canSave: valid && !saving,
      save: () => commit(),
      report: () => problems.current?.(),
    }
  );

  const handleChange = (patch: Partial<NewClient>) =>
    setClient((current) => ({ ...current, ...patch }));

  /**
   * Adds the client and says whether it landed, leaving where to go next to
   * whoever asked for it: the Save row goes to the roster, the rail's prompt
   * carries on to wherever it was pointing. It must not navigate itself, or the
   * prompt's answer would take somebody somewhere they did not ask for.
   */
  const commit = async (): Promise<boolean> => {
    if (saving) return false;

    // The dropdowns hold names; the endpoint takes the ids behind them. A name
    // with no id left to match means a catalog list changed while this form
    // sat open — worth naming the field rather than posting a broken body.
    const result = toCreateClientPayload(client, lookups);
    if (!result.ok) {
      toast.error(
        "Some options are out of date",
        `Pick ${result.unresolved.join(" and ")} again.`
      );
      return false;
    }

    setSaving(true);

    try {
      await createClient(getToken, result.payload);
    } catch (error) {
      toast.error(
        "Could not add this client",
        error instanceof ApiError
          ? error.message
          : "We could not reach the server. Check your connection and try again."
      );
      console.error("adding a client failed:", error);
      // Left on the form with everything still in it, which is the only place
      // the details can be corrected and sent again.
      setSaving(false);
      return false;
    }

    // Raised before the navigation, and it outlives it: the toast provider
    // sits above the router, so the message is still standing when the list
    // paints. That is the screen the confirmation belongs on — this page is
    // gone by the time anyone could read it here.
    const name = `${client.firstName} ${client.lastName}`.trim();
    toast.success("Client added", `${name} is now on your client list.`);

    // No `setSaving(false)`: the page is leaving either way, and setting state
    // on the way out is a warning about updating a component that has gone.
    return true;
  };

  const handleSave = async () => {
    if (await commit()) navigate(ROUTES.myClients);
  };

  return (
    // The page grows with the form rather than being pinned to the viewport,
    // so what scrolls is the whole page — under the shell's own scrollbar, on
    // the far right of the screen. `min-h-full` keeps it filling the viewport
    // when the form is short, which is what holds the action bar at the foot
    // of the screen rather than partway up it.
    //
    // No bottom padding on purpose: the action bar has to sit flush on the
    // foot of the shell, so it is the form that supplies the space under the
    // buttons rather than the page leaving a strip beneath the bar.
    <div className="min-h-full flex flex-col px-4 pt-5 sm:px-6 lg:px-8 3xl:px-12 3xl:pt-8">
      <PageHeader
        title="Add new client"
        breadcrumbs={[
          { label: "My clients", to: ROUTES.myClients },
          { label: "Add new client" },
        ]}
      />

      <AddClientForm
        client={client}
        onChange={handleChange}
        errors={errors}
        lookups={lookups}
        subscription={{ plans: sellable, status: plansStatus }}
        saving={saving}
        onSave={handleSave}
        onCancel={() => navigate(ROUTES.myClients)}
        problems={problems}
      />

      {/* The way out goes back to the roster rather than closing onto the form,
          which is the difference between this and the same prompt on the list
          screen: there, dismissing leaves them on a screen that still works.
          Here it would leave them in front of a form they cannot finish.

          The plan is asked for first where both are missing — the order the My
          brand tabs hold them, and the order the list screen asks in. */}
      {noPlans && (
        <PlansRequiredModal
          onCreatePlan={() => navigate(BRAND_PLANS_PATH)}
          onDismiss={() => navigate(ROUTES.myClients)}
          dismissLabel="Back to my clients"
        />
      )}

      {!noPlans && noServices && (
        <ServicesRequiredModal
          onCreateService={() => navigate(BRAND_SERVICES_PATH)}
          onDismiss={() => navigate(ROUTES.myClients)}
          dismissLabel="Back to my clients"
        />
      )}
    </div>
  );
};

export default AddClientPage;
